/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AccessControlTab.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 23-09-2024
 * @description : To give co-owner/editor/viewer access to the project/zones/ee component/software module
 */
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import customConsole from "../../../config/customConsole";
import { HandleApiActions, labCar } from "../../../redux/actions";
import {
  Box,
} from "@mui/material";
import {
  EEDetails,
  ModuleAccessTypes,
  ProjectDetails,
  SelectModules,
  SWMDetails,
  VariantDetailsBox,
  VehicleComponentDetails,
  VehicleSystemDetails,
} from "../../../components";
import { useLocation } from "react-router-dom";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import AccessOverviewDrawer from "../ProjectAccessDrawer";

function AccessControlTab({
  selectedProjectDetails,
  projectStatus,
  moduleTypes,
  accessRequestStatus,
}) {
  const dispatch = useDispatch();

  const location = useLocation();

  // New Requests Buttons
  const [selectedPaBtn, setSelectedPaBtn] = useState(moduleTypes?.PROJECT);
  // Co-owners state
  // const [coOwnersOptionsList, setCoOwnersOptionsList] = useState([]);
  // const [selectedCoOwner, setSelectedCoOwner] = useState(null);

  // Access type
  // const [selectedAtBtn, setSelectedAtBtn] = useState(accessTypes?.COOWNER);

  // Variant States
  const [variantDetails, setVariantDetails] = useState([]);
  const [variantOptionsList, setVariantOptionsList] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedVariantDetails, setSelectedVariantDetails] = useState(null);

  // Zones States
  const [zoneOptionsList, setZoneOptionsList] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedZoneDetails, setSelectedZoneDetails] = useState(null);

  // EE Components States
  const [zoneEEDetails, setZoneEEDetails] = useState([]);
  const [eeOptionsList, setEEOptionsList] = useState([]);
  const [selectedEE, setSelectedEE] = useState(null);
  const [selectedEEDetails, setSelectedEEDetails] = useState(null);

  // Software Modules States
  const [eeSwmDetails, setEESwmDetails] = useState([]);
  const [swmOptionsList, setSWMOptionsList] = useState([]);
  const [selectedSWM, setSelectedSWM] = useState(null);
  const [selectedSWMDetails, setSelectedSWMDetails] = useState(null);

  // Vehicle Systems States
  const [vehicleSystemDetails, setVehicleSystemDetails] = useState([]);
  const [vehicleSystemOptionsList, setVehicleSystemOptionsList] = useState([]);
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState(null);
  const [selectedVehicleSystemDetails, setSelectedVehicleSystemDetails] =
    useState(null);

  // Vehicle Components States
  const [vehicleComponentDetails, setVehicleComponentDetails] = useState([]);
  const [vehicleComponentOptionsList, setVehicleComponentOptionsList] =
    useState([]);
  const [selectedVehicleComponent, setSelectedVehicleComponent] =
    useState(null);
  const [selectedVehicleComponentDetails, setSelectedVehicleComponentDetails] =
    useState(null);

  //
  const [reasonForAccess, setReasonForAccess] = useState("");

  const [showOverview, setShowOverview] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [accessControlData, setAccessControlData] = useState({});

  useEffect(() => {
    console.log("selectedPaBtn:", selectedPaBtn);
    console.log("moduleTypes:", moduleTypes);
  }, [selectedPaBtn, moduleTypes]);

  const handleCloseDrawer = () => {
    setShowOverview(false);
    setAccessControlData({});
  };

  const handleAccessBtnClick = (params) => {
    console.log("--handleAccessBtnClick--", params);
    customConsole("handleAccessBtnClick: ");
    customConsole(params);

    setAccessControlData(params);
    setShowOverview(true);
  };

  // Functions to handle data //
  // Get Variant Details
  const getVariantDetails = async ({ projectId }) => {
    let apiDetails = labCar.get_variant_details;

    customConsole({
      ...apiDetails,
      params: {
        project_id: projectId,
        is_complete_details_required: false,
        // is_for_access: true,
      },
    });

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
          is_complete_details_required: true,
          // is_for_access: true,
        },
      }),
    );

    if (resp.error) {
      setVariantDetails([]);
    } else {
      setVariantDetails(resp.data);
    }
  };

  // Get EE Components Details
  const getZoneEEDetails = async ({ projectId, variantId, zonePosition }) => {
    let apiDetails = labCar.get_ee_components_pa;

    customConsole({
      ...apiDetails,
      params: {
        project_id: projectId,
        variant_id: variantId,
        ...(zonePosition && { zone_position: zonePosition }),
        // is_for_req_access: true,
        status: true,
      },
    });

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
          variant_id: variantId,
          ...(zonePosition && { zone_position: zonePosition }),
          // is_for_req_access: true,
          status: true,
        },
      }),
    );

    if (resp.error) {
      setZoneEEDetails([]);
    } else {
      setZoneEEDetails(resp.data);
    }
  };

  // Get Software Module Details
  const getEESwmDetails = async ({ projectId, variantId, eeId }) => {
    let apiDetails = labCar.get_sw_modules_pa;

    customConsole({
      ...apiDetails,
      params: {
        project_id: projectId,
        variant_id: variantId,
        ee_id: eeId,
        // is_for_req_access: true,
        status: true,
      },
    });

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
          variant_id: variantId,
          ee_id: eeId,
          // is_for_req_access: true,
          status: true,
        },
      }),
    );

    if (resp.error) {
      setEESwmDetails([]);
    } else {
      setEESwmDetails(resp.data);
    }
  };

  /**
   * @author : Narender
   * @Date : 03-03-2025
   * @description : Fetches vehicle systems Details
   * @param {project_id, variant_id, zone_position}
   * @return {msg: "", data: [], error: true | false}
   */
  const getVehicleSystemsDetails = async ({
    projectId,
    variantId,
    zonePosition,
  }) => {
    const apiDetails = labCar.get_vehicle_systems_all;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          ...(zonePosition && { zone_position: zonePosition }), // Add zone position if zonal
          is_for_req_access: true,
        },
      }),
    );
    if (resp.error) {
      setVehicleSystemDetails([]);
    } else {
      setVehicleSystemDetails(resp.data);
    }
  };
  /**
   * @author : Narender
   * @Date : 03-03-2025
   * @description : Fetches vehicle components Details
   * @param {project_id, variant_id, vs_id}
   * @return {msg: "", data: [], error: true | false}
   */
  const getVehicleComponentsDetails = async ({
    projectId,
    variantId,
    vsId,
  }) => {
    const apiDetails = labCar.get_vehicle_components_all;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: projectId,
          variant_id: variantId,
          vs_id: vsId,
          is_for_req_access: true,
        },
      }),
    );
    if (resp.error) {
      setVehicleComponentDetails([]);
    } else {
      setVehicleComponentDetails(resp.data);
    }
  };
  /* UseEffects */
  useEffect(() => {
    // Make the states to default when the project is changed
    // access type buttons
    setSelectedPaBtn(moduleTypes?.PROJECT);
    // Variant
    setVariantOptionsList([]);
    setSelectedVariant(null);
    setSelectedVariantDetails(null);
    // Zones
    setZoneOptionsList([]);
    setSelectedZone(null);
    setSelectedZoneDetails(null);
    // EE Components
    setEEOptionsList([]);
    setSelectedEE(null);
    setSelectedEEDetails(null);
    // Software Modules
    setSWMOptionsList([]);
    setSelectedSWM(null);
    setSelectedSWMDetails(null);
    // Vehicle Systems
    setVehicleSystemOptionsList([]);
    setSelectedVehicleSystem(null);
    setSelectedVehicleSystemDetails(null);
    // Vehicle Components
    setVehicleComponentOptionsList([]);
    setSelectedVehicleComponent(null);
    setSelectedVehicleComponentDetails(null);
  }, [selectedProjectDetails]);

  useEffect(() => {
    // When the selected Variant is changed, make the Zone state to default
    setZoneOptionsList([]);
    setSelectedZone(null);
    setSelectedZoneDetails(null);
  }, [selectedVariant]);

  useEffect(() => {
    // When the selected Zone is changed, make the EE Components state to default
    setEEOptionsList([]);
    setSelectedEE(null);
    setSelectedEEDetails(null);
    // When the selected Zone is changed, make the Vehicle Systems state to default
    setVehicleSystemOptionsList([]);
    setSelectedVehicleSystem(null);
    setSelectedVehicleSystemDetails(null);
  }, [selectedVariant, selectedZone]);

  useEffect(() => {
    // When the selected Zone/EE Component is changed, make the Software Modules state to default
    setSWMOptionsList([]);
    setSelectedSWM(null);
    setSelectedSWMDetails(null);
  }, [selectedVariant, selectedZone, selectedEE]);

  useEffect(() => {
    // When the selected Zone/Vehicle System is changed, make the Vehicle Component state to default
    setVehicleComponentOptionsList([]);
    setSelectedVehicleComponent(null);
    setSelectedVehicleComponentDetails(null);
  }, [selectedVariant, selectedZone, selectedVehicleSystem]);

  useEffect(() => {
    if (selectedProjectDetails?.project_id) {
      // Get variant details
      getVariantDetails({
        projectId: selectedProjectDetails?.project_id,
      });
    }
  }, [selectedProjectDetails]);

  useEffect(() => {
    // Get variant options list
    let filteredVariantOptionsList = variantDetails
      ?.map((el) => {
        return {
          value: el?.variant_id,
          label: el?.name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVariantOptionsList(filteredVariantOptionsList);

    // Select the first variant
    if (filteredVariantOptionsList?.length) {
      setSelectedVariant(filteredVariantOptionsList?.[0]?.value);
    }
  }, [variantDetails]);

  useEffect(() => {
    // Get variant details
    let filteredVariantDetails = variantDetails?.find(
      (el) => Number.parseInt(el?.variant_id) === Number.parseInt(selectedVariant),
    );
    setSelectedVariantDetails(filteredVariantDetails);
  }, [selectedVariant]);

  useEffect(() => {

    // If Centralized project, Get the EE Components Details, When the project is selected
    // If Centralized project, Get the Vehicle Systems Details, When the project is selected
    if (
      selectedProjectDetails?.project_id &&
      !selectedProjectDetails?.is_zonal &&
      selectedVariantDetails?.variant_id
    ) {
      getZoneEEDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
      });
      getVehicleSystemsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
      });
    }

    // Zones options list
    let filteredZoneOptionsList = selectedVariantDetails?.zone_details
      ?.map((el) => {
        return {
          // value: el?.sequence,
          value: el?.zone?.position,
          label: el?.zone?.zone_name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setZoneOptionsList(filteredZoneOptionsList);
    // Select the first zone, when the selected variant is changed
    if (filteredZoneOptionsList?.length) {
      setSelectedZone(filteredZoneOptionsList?.[0]?.value);
    }
  }, [selectedVariantDetails]);

  useEffect(() => {
    /* Update selected zone details based on the selected zone */
    let filteredSelectedZoneDetails =
      selectedVariantDetails?.zone_details?.find(
        (el) => el?.zone?.position === selectedZone,
      );
    setSelectedZoneDetails(filteredSelectedZoneDetails);

    // If Zonal project, Get the EE Components Details, When the zone is selected
    // If Zonal project, Get the Vehicle Systems Details, When the zone is selected
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedZone
    ) {
      getZoneEEDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        zonePosition: selectedZone,
      });
      getVehicleSystemsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        zonePosition: selectedZone,
      });
    }
  }, [selectedZone]);

  useEffect(() => {
    // EE Components Options List
    let filteredEEOptionsList = zoneEEDetails
      ?.map((el) => {
        return {
          value: el?.ee_id,
          label: el?.description,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setEEOptionsList(filteredEEOptionsList);

    // Select the first ee component, when the selected zone is changed
    if (filteredEEOptionsList?.length) {
      setSelectedEE(filteredEEOptionsList[0]?.value);
    }
  }, [zoneEEDetails]);

  useEffect(() => {
    // When EE Component is selected, Get the Software Module Details
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedEE
    ) {
      getEESwmDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        eeId: selectedEE,
      });
    }

    // Get Selected EE Component details
    let filteredSelectedEEDetails = zoneEEDetails?.find(
      (el) => Number.parseInt(el?.ee_id) === Number.parseInt(selectedEE),
    );
    setSelectedEEDetails(filteredSelectedEEDetails);
  }, [selectedEE]);

  useEffect(() => {
    // Software Module Options List
    let filteredSWMOptionsList = eeSwmDetails
      ?.map((el) => {
        return {
          value: el?.swm_id,
          label: el?.name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setSWMOptionsList(filteredSWMOptionsList);

    // Select the first software module, when the selected ee component is changed
    if (filteredSWMOptionsList?.length) {
      setSelectedSWM(filteredSWMOptionsList[0]?.value);
    }
  }, [eeSwmDetails]);

  useEffect(() => {
    // Get Selected Software Module Details
    let filteredEESwmDetails = eeSwmDetails?.find(
      (el) => Number.parseInt(el?.swm_id) === Number.parseInt(selectedSWM),
    );
    setSelectedSWMDetails(filteredEESwmDetails);
  }, [selectedSWM]);

  useEffect(() => {
    // Vehicle Systems Options List
    let filteredVehicleSystemOptionsList = vehicleSystemDetails
      ?.map((el) => {
        return {
          value: el?.id,
          label: el?.assembly_number,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVehicleSystemOptionsList(filteredVehicleSystemOptionsList);

    console.log(
      "filteredVehicleSystemOptionsListfilteredVehicleSystemOptionsListfilteredVehicleSystemOptionsListfilteredVehicleSystemOptionsListfilteredVehicleSystemOptionsList",
      filteredVehicleSystemOptionsList,
    );
    // Select the first Vehicle System, when the selected zone is changed
    if (filteredVehicleSystemOptionsList?.length) {
      setSelectedVehicleSystem(filteredVehicleSystemOptionsList[0]?.value);
    }
  }, [vehicleSystemDetails]);

  useEffect(() => {
    console.log(
      "selectedVehicleSystemselectedVehicleSystemselectedVehicleSystem",
      selectedVehicleSystem,
    );
    // When Vehicle System is selected, Get the Vehicle Component Details
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedVehicleSystem
    ) {
      getVehicleComponentsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        vsId: selectedVehicleSystem,
      });
    }

    // Get Selected Vehicle System details
    let filteredSelectedVehicleSystemDetails = vehicleSystemDetails?.find(
      (el) => Number.parseInt(el?.id) === Number.parseInt(selectedVehicleSystem),
    );
    console.log(
      "filteredSelectedVehicleSystemDetailsfilteredSelectedVehicleSystemDetailsfilteredSelectedVehicleSystemDetails",
      filteredSelectedVehicleSystemDetails,
    );
    setSelectedVehicleSystemDetails(filteredSelectedVehicleSystemDetails);
  }, [selectedVehicleSystem]);

  useEffect(() => {
    // Vehicle Component Options List
    let filteredVehicleComponentOptionsList = vehicleComponentDetails
      ?.map((el) => {
        return {
          value: el?.id,
          label: el?.component_number,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVehicleComponentOptionsList(filteredVehicleComponentOptionsList);

    // Select the first Vehicle Component, when the selected vehicle system is changed
    if (filteredVehicleComponentOptionsList?.length) {
      setSelectedVehicleComponent(
        filteredVehicleComponentOptionsList[0]?.value,
      );
    }
  }, [vehicleComponentDetails]);

  useEffect(() => {
    // Get Selected Software Module Details
    let filteredVehicleComponentDetails = vehicleComponentDetails?.find(
      (el) => Number.parseInt(el?.id) === Number.parseInt(selectedVehicleComponent),
    );
    setSelectedVehicleComponentDetails(filteredVehicleComponentDetails);
  }, [selectedVehicleComponent]);

  return (
    // New Request
    <Box sx={styles.nrContainer}>
      {/* Module Access Types */}
      <ModuleAccessTypes
        selectedProjectDetails={selectedProjectDetails}
        selectedPaBtn={selectedPaBtn}
        setSelectedPaBtn={setSelectedPaBtn}
        moduleTypes={moduleTypes}
      />

      {/* Request access */}
      <Box sx={styles.nrMainContainer}>
        {/* Project Details */}
        <ProjectDetails
          selectedProjectDetails={selectedProjectDetails}
          projectStatus={projectStatus}
        />

        {/* Variant Details */}
        <VariantDetailsBox selectedVariantDetails={selectedVariantDetails} />

        {/* Select Zone/EE Component/Software Module */}
        <SelectModules
          selectedProjectDetails={selectedProjectDetails}
          selectedPaBtn={selectedPaBtn}
          moduleTypes={moduleTypes}
          variantOptionsList={variantOptionsList}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          zoneOptionsList={zoneOptionsList}
          selectedZone={selectedZone}
          setSelectedZone={setSelectedZone}
          eeOptionsList={eeOptionsList}
          selectedEE={selectedEE}
          setSelectedEE={setSelectedEE}
          swmOptionsList={swmOptionsList}
          selectedSWM={selectedSWM}
          setSelectedSWM={setSelectedSWM}
          vehicleSystemOptionsList={vehicleSystemOptionsList}
          selectedVehicleSystem={selectedVehicleSystem}
          setSelectedVehicleSystem={setSelectedVehicleSystem}
          vehicleComponentOptionsList={vehicleComponentOptionsList}
          selectedVehicleComponent={selectedVehicleComponent}
          setSelectedVehicleComponent={setSelectedVehicleComponent}
        />

        {/* EE Component Details */}
        {selectedPaBtn === moduleTypes?.EE && selectedEE && (
          <EEDetails selectedEEDetails={selectedEEDetails} />
        )}

        {/* Software Module Details */}
        {selectedPaBtn === moduleTypes?.SWM && selectedSWM && (
          <SWMDetails selectedSWMDetails={selectedSWMDetails} />
        )}

        {/* Vehicle System Details */}
        {selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM &&
          selectedVehicleSystem && (
            <VehicleSystemDetails
              selectedVehicleSystemDetails={selectedVehicleSystemDetails}
            />
          )}

        {/* Vehicle Component Details */}
        {selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT &&
          selectedVehicleComponent && (
            <VehicleComponentDetails
              selectedVehicleComponentDetails={selectedVehicleComponentDetails}
            />
          )}

        {/* Access type */}
        <Box sx={styles.acATContainer}>
          <Box sx={styles.acATbContainer}>
            <CustomButtonV2
              size={"small"}
              disabled={
                // Project Access Validation
                (selectedPaBtn === moduleTypes?.PROJECT &&
                  !selectedProjectDetails?.project_id) ||
                // Variant Validation
                (selectedPaBtn === moduleTypes?.VARIANT &&
                  (!selectedProjectDetails?.project_id || !selectedVariant)) ||
                // Zone Access Validation
                (selectedPaBtn === moduleTypes?.ZONE &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedZone)) ||
                // EE Component Access Validation
                (selectedPaBtn === moduleTypes?.EE &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedEEDetails?.ee_id)) ||
                // Software Module Access Validation
                (selectedPaBtn === moduleTypes?.SWM &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedEEDetails?.ee_id ||
                    !selectedSWMDetails?.swm_id)) ||
                // Vehicle System Validation
                (selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedVehicleSystem)) ||
                // Vehicle Component Validation
                (selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT &&
                  (!selectedProjectDetails?.project_id ||
                    !selectedVariant ||
                    !selectedVehicleSystem ||
                    !selectedVehicleComponent))
              }
              btnName={"Access Details"}
              handleOnClick={() =>
                handleAccessBtnClick({
                  access_type: selectedPaBtn,
                  project_id: selectedProjectDetails?.project_id,
                  variant_id: selectedVariantDetails?.variant_id,
                  zone_position: selectedZoneDetails?.zone?.position,
                  ee_id: selectedEEDetails?.ee_id,
                  swm_id: selectedSWMDetails?.swm_id,
                  vs_id: selectedVehicleSystemDetails?.id,
                  vc_id: selectedVehicleComponentDetails?.id,
                })
              }
            />
          </Box>
        </Box>
      </Box>

      <AccessOverviewDrawer
        open={showOverview}
        data={accessControlData}
        handleClose={() => setShowOverview(false)}
        onManageAccess={() => {
          console.log("OnManageAccess");
          setShowOverview(false);
        }}
      />

      {/* <GrantAccessDrawer
        open={showGrant}
        onClose={() => setShowGrant(false)}
        context={accessControlData}
      /> */}
    </Box>
  );
}

export default AccessControlTab;

// Styles
const styles = {
  nrContainer: {
    width: "100%",
    display: "flex",
    p: 2,
    mt: 3,
    // border: "1px solid grey",
  },
  nrMainContainer: {
    width: "100%",
    border: "1px solid grey",
    ml: 3,
    px: 2,
    pt: 0.6,
    pb: 2,
  },
  acATContainer: {
    mt: 4,
    mb: 0.4,
  },
  acATbContainer: {},
};
